import PropTypes from 'prop-types'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import Decimal from 'decimal.js'

import DropDownPicker from 'components/Picker'
import { getBalance, getVerifiedEmailAddresses, getVerifiedPhoneNumbers } from 'models/ProfileInfo/selectors'

import { STRING } from 'resources'
import { APP_LINK } from 'resources/urls'

import formatCurrency from 'utils/format-currency'
import formatPoints from 'utils/format-points'

import PrimaryLink from 'units/PrimaryLink'

import {
  CancelButton,
  RedemptionProcessNote,
  RedemptionProcessNoteContainer,
  RedemptionSelectionButton,
  RedemptionSelectionContainer,
  RedemptionSelectionOptions,
} from './styles'

const GiftCardAmountSelector = ({
  reward,
  redemptionDestination,
  setRedemptionDestination,
  selectedAmount,
  setSelectedAmount,
  onRequest,
}) => {
  const balance = useSelector(getBalance)
  const verifiedEmailAddresses = useSelector(getVerifiedEmailAddresses)
  const verifiedPhoneNumbers = useSelector(getVerifiedPhoneNumbers)

  const validDestinations = useMemo(() => {
    const emailAddresses = verifiedEmailAddresses.map((entry) => entry.emailAddress)
    const phoneNumbers = verifiedPhoneNumbers.map((entry) => entry.phoneNumber)
    return [...emailAddresses, ...phoneNumbers]
  }, [verifiedEmailAddresses, verifiedPhoneNumbers])

  const redemptionOptionComponents = useMemo(
    () =>
      reward.redemptionValues
        .filter((val) => new Decimal(val).lte(balance))
        .map((optionAmount) => (
          <option key={optionAmount} value={optionAmount}>
            {formatPoints(optionAmount)}
          </option>
        )),
    [reward.redemptionValues, balance],
  )

  const handleRequestAmountChange = useCallback(
    (event) => {
      const newValue = event.target.value
      setSelectedAmount(newValue)
    },
    [setSelectedAmount],
  )

  return (
    <RedemptionSelectionContainer>
      <RedemptionSelectionOptions onChange={handleRequestAmountChange} value={selectedAmount}>
        {redemptionOptionComponents}
      </RedemptionSelectionOptions>
      <RedemptionSelectionButton onPress={onRequest}>
        {STRING.wallet.redemptions.giftCardRedemption.redeemSelectionButton}
      </RedemptionSelectionButton>
      <CancelButton to={APP_LINK} tag={PrimaryLink}>
        {STRING.wallet.redemptions.giftCardRedemption.redeemCancelButton}
      </CancelButton>
      <RedemptionProcessNoteContainer>
        <RedemptionProcessNote>
          {STRING.wallet.redemptions.giftCardRedemption.accountSelector.title}
          <DropDownPicker
            onUpdate={setRedemptionDestination}
            value={redemptionDestination}
            pickerItems={validDestinations}
            placeholder={STRING.wallet.redemptions.giftCardRedemption.accountSelector.placeholder}
          />
        </RedemptionProcessNote>
        <RedemptionProcessNote>
          {STRING.wallet.redemptions.giftCardRedemption.pointsToDollar(
            formatPoints(1, true).toLowerCase(),
            formatCurrency(1, true),
          )}
        </RedemptionProcessNote>
        <RedemptionProcessNote>{STRING.wallet.redemptions.giftCardRedemption.redeemProcessNote}</RedemptionProcessNote>
      </RedemptionProcessNoteContainer>
    </RedemptionSelectionContainer>
  )
}

GiftCardAmountSelector.propTypes = {
  reward: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    categoryId: PropTypes.string.isRequired,
    isTop: PropTypes.bool.isRequired,
    imageUrl: PropTypes.string.isRequired,
    redemptionValues: PropTypes.arrayOf(PropTypes.string).isRequired,
    currentPrice: PropTypes.string,
    dayChange: PropTypes.string,
  }).isRequired,
  redemptionDestination: PropTypes.string.isRequired,
  setRedemptionDestination: PropTypes.func.isRequired,
  selectedAmount: PropTypes.string.isRequired,
  setSelectedAmount: PropTypes.func.isRequired,
  onRequest: PropTypes.func.isRequired,
}

export default GiftCardAmountSelector
